// src/components/files/BlocPhotos.tsx — SCE Issues
// Le bloc Photos de la fiche (09/10), DANS la description : TOUTES les photos
// du problème, en deux groupes séparés d'un trait fin, « Déclaration » puis
// « Activité ». Une photo de l'activité porte les initiales de son auteur ;
// une photo annotée se montre annotée, avec une pastille jaune.
// Maquette validée : https://claude.ai/artifact/DcEcrXUbuMHMLCAfPxV5TJ

import { ExternalLink, PenLine } from 'lucide-react'
import { Vignette } from '@/components/files/PieceJointeGallery'
import { PastillePersonne } from '@/components/personnes/PastillePersonne'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import type { PhotoFiche } from '@/utils/rattachementPhotos'

/** Vignettes : plus grandes sur PC, où la place ne manque pas. */
const TAILLE = 92
const TAILLE_PC = 124

const styleGroupe = {
  fontSize: 11,
  letterSpacing: 1,
  textTransform: 'uppercase',
  color: 'var(--color-text-secondary)',
  fontWeight: 500,
} as const

export function BlocPhotos({
  photos,
  legacyUrl,
  onOuvrir,
}: {
  photos: PhotoFiche[]
  /** Ancienne pièce jointe unique (field_11) : lien en fin de rangée. */
  legacyUrl?: string
  /** Ouvre la visionneuse sur la photo de ce rang. */
  onOuvrir: (rang: number) => void
}): JSX.Element | null {
  const taille = useMediaQuery('(min-width: 1100px)') ? TAILLE_PC : TAILLE
  if (photos.length === 0 && !legacyUrl) return null

  const probleme = photos.filter((p) => p.origine === 'probleme')
  const activite = photos.filter((p) => p.origine === 'activite')

  const tuile = (p: PhotoFiche, rang: number): JSX.Element => (
    <Vignette
      key={p.pj.id}
      pj={p.annotation?.pj ?? p.pj}
      size={taille}
      rayon={10}
      onClick={() => onOuvrir(rang)}
      libelle={
        p.origine === 'probleme'
          ? `Photo du problème ${rang + 1} sur ${probleme.length}`
          : `Photo de l’activité${p.auteur ? `, envoyée par ${p.auteur}` : ''}`
      }
      badges={
        <>
          {p.annotation && (
            <span
              aria-label="Annotée"
              className="flex items-center justify-center"
              style={{
                position: 'absolute',
                top: 6,
                right: 6,
                width: 24,
                height: 24,
                borderRadius: 12,
                background: 'var(--color-yellow)',
                color: 'var(--color-black)',
              }}
            >
              <PenLine size={13} strokeWidth={2.4} />
            </span>
          )}
          {p.origine === 'activite' && p.auteur ? (
            <PastillePersonne
              nom={p.auteur}
              upn={p.auteurUpn}
              taille={24}
              fond="rgba(0,0,0,0.72)"
              taillePolice={10}
              style={{ position: 'absolute', left: 6, bottom: 6 }}
            />
          ) : null}
        </>
      }
    />
  )

  // Posé dans la description (retour du 09/10) : ni cadre, ni titre, ni
  // « Tout voir » ; les deux mini sous-titres suffisent.
  return (
    <section aria-label="Photos">
      <div className="flex" style={{ gap: 14, overflowX: 'auto', paddingBottom: 2 }}>
        {probleme.length > 0 && (
          <div className="flex flex-col" style={{ gap: 8, flexShrink: 0 }}>
            <span style={styleGroupe}>Déclaration</span>
            <div className="flex" style={{ gap: 8 }}>
              {probleme.map((p, i) => tuile(p, i))}
            </div>
          </div>
        )}

        {probleme.length > 0 && activite.length > 0 && (
          <div aria-hidden="true" style={{ width: 1, background: 'var(--color-border)', marginTop: 22, flexShrink: 0 }} />
        )}

        {activite.length > 0 && (
          <div className="flex flex-col" style={{ gap: 8, flexShrink: 0 }}>
            <span style={styleGroupe}>Activité</span>
            <div className="flex" style={{ gap: 8 }}>
              {activite.map((p, i) => tuile(p, probleme.length + i))}
            </div>
          </div>
        )}

        {legacyUrl && (
          <a
            href={legacyUrl}
            target="_blank"
            rel="noreferrer"
            title="Pièce jointe initiale"
            aria-label="Pièce jointe initiale"
            className="flex items-center justify-center"
            style={{
              alignSelf: 'flex-end',
              width: taille,
              height: taille,
              borderRadius: 10,
              backgroundColor: 'var(--color-gray-5)',
              border: '1px solid var(--color-border)',
              color: 'var(--color-teal)',
              flexShrink: 0,
            }}
          >
            <ExternalLink size={20} />
          </a>
        )}
      </div>
    </section>
  )
}
